import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { ChannelsService } from '../../channels.service';
import { ChannelFormData } from '../../channels.model';

export type CodeValidationState = 'idle' | 'checking' | 'valid' | 'taken';

@Component({
  selector: 'app-channel-identity-tab',
  standalone: false,
  templateUrl: './channel-identity-tab.component.html',
  styleUrl: './channel-identity-tab.component.scss'
})
export class ChannelIdentityTabComponent implements OnInit, OnDestroy {
  private channelsService = inject(ChannelsService);
  private codeCheckSubject = new Subject<string>();
  private _sub!: Subscription;

  @Input() formData!: ChannelFormData;
  @Input() submitted = false;
  @Input() nameError = false;
  @Input() codeError = false;
  @Output() nameErrorChange = new EventEmitter<boolean>();
  @Output() codeErrorChange = new EventEmitter<boolean>();
  @Output() codeValidationChange = new EventEmitter<CodeValidationState>();

  codeValidationState: CodeValidationState = 'idle';

  ngOnInit(): void {
    this._sub = this.codeCheckSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(code => {
        if (!code || code.length < 2) {
          this.codeValidationState = 'idle';
          this.codeValidationChange.emit(this.codeValidationState);
          return of(null);
        }
        this.codeValidationState = 'checking';
        this.codeValidationChange.emit(this.codeValidationState);
        return this.channelsService.checkCodeAvailability(code).pipe(
          catchError(() => of({ available: true }))
        );
      })
    ).subscribe(result => {
      if (result !== null) {
        this.codeValidationState = result.available ? 'valid' : 'taken';
        this.codeValidationChange.emit(this.codeValidationState);
      }
    });
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
    this.codeCheckSubject.complete();
  }

  onCodeInput(value: string): void {
    this.formData.code = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    this.codeErrorChange.emit(false);
    if (this.formData.code.length >= 2) {
      this.codeValidationState = 'checking';
      this.codeCheckSubject.next(this.formData.code);
    } else {
      this.codeValidationState = 'idle';
    }
    this.codeValidationChange.emit(this.codeValidationState);
  }
}
